import { Component, OnInit, AfterViewInit, OnDestroy, NgZone } from '@angular/core';
import { App } from '@capacitor/app';
import { PluginListenerHandle } from '@capacitor/core';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { Observer } from 'gsap/Observer';
import { AuthLandingPageService } from '../../providers/auth-landing-page.service';
import { SettingsService } from '../../providers/settings.service';
import { FirebaseAuthService } from '../../providers/firebase-auth.service';
import { UtilityFunctionsService } from '../../providers/utility-functions.service';
import { environment } from 'src/environments/environment';
import { Observable } from 'rxjs';
import { fullscreenPanelAnimation } from '../../providers/animations-list';

gsap.registerPlugin(ScrollTrigger, Observer);

@Component({
  selector: 'app-landing2025',
  templateUrl: './landing2025.component.html',
  styleUrls: ['./landing2025.component.scss'],
  animations: [fullscreenPanelAnimation]
})
export class Landing2025Component implements OnInit, AfterViewInit, OnDestroy {
  public isFirstDesktopVideoReady = false;
  public isFirstMobileVideoReady = false;
  private videos: HTMLVideoElement[];
  private resizeHandler?: () => void;
  private orientationHandler?: () => void;
  private visibilityHandler?: () => void;
  private hasUserInteraction = false;
  private appStateListener: PluginListenerHandle | null = null;

  // Mobile splash screen state
  public isMobileSplashVisible$: Observable<boolean>;
  public mobileSplashConfig$: Observable<{ showSignInForm: boolean; redirectAfterAuth: boolean }>;
  public isMobilePlatform: boolean;
  public detectedMobileDevice: 'android' | 'ios' | null = null;

  // Animation constants - easier to understand and modify
  private readonly ANIMATION_TIMING = {
    // Phase 1A: Text1 exit (goes up)
    TEXT1_EXIT_START: 0.3,
    TEXT1_EXIT_DURATION: 0.3,

    // Phase 1B: Text2 entrance (comes from bottom)
    TEXT2_ENTER_START: 0.5,
    TEXT2_ENTER_DURATION: 0.4,

    // Phase 2: Show monitors and shrink container
    PHASE_2_START: 0.4,
    PHASE_2_DURATION: 0.1,

    // Phase 3: Background-videos animation (appear from center)
    PHASE_3_START: 0.5,
    PHASE_3_DURATION: 0.5,

    // Phase 4A: Text2 exit (goes up)
    TEXT2_EXIT_START: 1.4,
    TEXT2_EXIT_DURATION: 0.3,

    // Phase 4B: Text3 entrance (comes from bottom)
    TEXT3_ENTER_START: 1.6,
    TEXT3_ENTER_DURATION: 0.4,

    // Phase 4: Other transitions
    PHASE_4_START: 1.5,
    PHASE_4_DURATION: 0.5,

    // Phase 5: Text4 appearance
    TEXT4_DELAY: 1.9,

    // Phase 6: Hide background-videos
    PHASE_6_DURATION: 0.2,
  };

  constructor(
    private zone: NgZone,
    public authModalService: AuthLandingPageService,
    private settingsService: SettingsService,
    public firebaseAuth: FirebaseAuthService,
    public utilityService: UtilityFunctionsService
  ) {
    // Initialize mobile splash screen observables
    this.isMobileSplashVisible$ = this.authModalService.isMobileSplashVisible$;
    this.mobileSplashConfig$ = this.authModalService.mobileSplashConfig$;
    this.isMobilePlatform = environment.type === 'mobile';
  }

  /**
   * Get media URL with environment-specific base URL
   */
  getMediaUrl(path: string, addMini?: boolean): string {
    if (addMini && window.innerWidth < 768) {
      return `${environment.landingMediaBaseUrl}${path.replace('.mp4', '_mini.mp4')}`;
    }
    return `${environment.landingMediaBaseUrl}${path}`;
  }

  ngOnInit(): void {
    this.detectedMobileDevice = this.utilityService.detectMobileDevice();

    if(!this.firebaseAuth.currentUser) {
        this.firebaseAuth.initializeGoogleOneTap(
            () => {
                this.utilityService.showSnackBar('Logged in with Google', 2000);
            },
            (err) => {
                console.log(err);
                // Silent error or retry logic
            }
        );
    }

    if (this.isMobilePlatform) {
      setTimeout(() => {
      this.settingsService.hideSplashScreen();
      }, 1000);
    }
  }

  ngAfterViewInit(): void {
    this.videos = [
      ...gsap.utils.toArray('.background-videos video'),
      ...gsap.utils.toArray('.monitor-video'),
      ...gsap.utils.toArray('.first-screen-phone-video'),
      ...gsap.utils.toArray('.third-screen-phone-video'),
    ] as HTMLVideoElement[];

    this.setupVideoAutoplay();

    // Special handling for Capacitor native apps
    if (this.isMobilePlatform) {
      this.setupCapacitorVideoControls();
      // Force initial play for mobile app after a short delay
      setTimeout(() => {
        this.hasUserInteraction = true;
        this.checkAndPlayVideos();
      }, 300);
    } else if (window.innerWidth <= 768) {
      // For mobile web, try to autoplay muted videos.
      // It might be blocked by the browser, but it's the best we can do.
      setTimeout(() => this.attemptInitialAutoplay(), 300);
    }

    // Pre-computation for the mobile version
    if (this.isMobileOrTabletPortrait()) {
      this.prepareMobileLayout();
    }

    // Defer GSAP init until layout/media are ready
    this.zone.runOutsideAngular(async () => {
      await this.waitForLayoutStability(800);

      // Use double RAF to ensure layout is fully committed by the browser
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          this.initScrollAnimation();

          // Force browser to recalculate layout before refresh
          void document.body.offsetHeight;

          // Initial refresh after animations are created
          ScrollTrigger.refresh();

          // Final refresh after paint to catch any late-rendering elements (especially our-pricing-section)
          setTimeout(() => {
            ScrollTrigger.refresh(true);
            console.log('✅ LANDING: Final ScrollTrigger refresh complete');
          }, 150);
        });
      });

      this.resizeHandler = () => {
        if (this.isMobileOrTabletPortrait()) {
          this.prepareMobileLayout();
        } else {
          this.resetMobileLayout();
        }
        ScrollTrigger.refresh();
      };
      this.orientationHandler = () => setTimeout(() => ScrollTrigger.refresh(), 150);

      window.addEventListener('resize', this.resizeHandler, { passive: true });
      window.addEventListener('orientationchange', this.orientationHandler as any, { passive: true });
    });
  }

  ngOnDestroy(): void {
    console.log('🧹 LANDING: ngOnDestroy - Starting cleanup');

    // Clean up Capacitor listener
    this.appStateListener?.remove();

    try {
      if (this.resizeHandler) window.removeEventListener('resize', this.resizeHandler);
      if (this.orientationHandler) window.removeEventListener('orientationchange', this.orientationHandler as any);
      // UNCOMMENT THIS IN CASE OF PROBLEMS WITH UPDATES
      // if (this.visibilityHandler) document.removeEventListener('visibilitychange', this.visibilityHandler as any);
    } catch { /* noop */ }

    // ⚠️ CRITICAL: Full ScrollTrigger cleanup sequence
    ['landingPinDesktop', 'landingPinMobile', 'advertisingReveal', 'headerInvert', 'zIndexControl', 'landingFadeOut']
      .forEach(id => {
        const trigger = ScrollTrigger.getById(id);
        if (trigger) {
          console.log(`🧹 LANDING: Killing trigger ${id}`);
          trigger.kill(true); // true = revert changes
        }
      });
    ScrollTrigger.killAll(false); // false = don't revert (we'll handle it manually)
    document.querySelectorAll('.pin-spacer').forEach(spacer => {
      console.log('🧹 LANDING: Removing pin-spacer');
      spacer.remove();
    });
    ScrollTrigger.clearScrollMemory();
    ScrollTrigger.normalizeScroll(false);
    ScrollTrigger.refresh();
    console.log('✅ LANDING: Cleanup complete, active triggers:', ScrollTrigger.getAll().length);
  }

  private async waitForLayoutStability(timeoutMs = 600): Promise<void> {
    const fontsReady = (document as any).fonts?.ready?.catch?.(() => undefined) ?? Promise.resolve();
    const media = Array.from(document.querySelectorAll('img, video')) as Array<HTMLImageElement | HTMLVideoElement>;
    const mediaReady = Promise.all(media.map(el => new Promise<void>(res => {
      if (el instanceof HTMLImageElement) {
        if (el.complete) return res();
        el.addEventListener('load', () => res(), { once: true });
        el.addEventListener('error', () => res(), { once: true });
      } else {
        const v = el as HTMLVideoElement;
        if (v.readyState >= 1) return res();
        v.addEventListener('loadedmetadata', () => res(), { once: true });
        v.addEventListener('error', () => res(), { once: true });
      }
    })));

    // CRITICAL: Wait for our-pricing-section to be fully rendered and measured
    const advertisingSectionReady = new Promise<void>(res => {
      const checkSection = () => {
        const section = document.querySelector('.our-pricing-section');
        if (section && section.clientHeight > 0) {
          res();
        } else {
          requestAnimationFrame(checkSection);
        }
      };
      requestAnimationFrame(checkSection);
    });

    const timeout = new Promise<void>(res => setTimeout(res, timeoutMs));
    await Promise.race([Promise.all([fontsReady, mediaReady, advertisingSectionReady]), timeout]);
  }

  public mobileVideoReady(event: any): void {
    console.log('Mobile video ready');
    this.playVideo(event);
    this.isFirstMobileVideoReady = true;
  }

  public desktopVideoReady(event: any): void {
    console.log('Desktop video ready');
    this.playVideo(event);
    this.isFirstDesktopVideoReady = true;
  }

  /**
   * Sets up video autoplay after first user interaction
   */
  private setupVideoAutoplay(): void {
    const onFirstInteraction = () => {
      this.hasUserInteraction = true;
      this.checkAndPlayVideos();
    };

    // Listen for first interaction to start videos (browser policy)
    document.addEventListener('click', onFirstInteraction, { once: true });
    document.addEventListener('scroll', onFirstInteraction, { once: true });
    document.addEventListener('touchstart', onFirstInteraction, { once: true });
  }

  /**
   * Checks video visibility and controls their playback
   */
  private checkAndPlayVideos(): void {
    this.videos.forEach((video) => {
      if (!this.hasUserInteraction) return;

      // Simple visibility check - play if visible, pause if not
      const rect = video.getBoundingClientRect();
      const isVisible = rect.top < window.innerHeight && rect.bottom > 0;

      if (isVisible && video.paused) {
        video.muted = true;
        const playPromise = video.play();
        if (playPromise !== undefined) {
          playPromise.catch(() => { /* suppressed; will retry after interaction/visibility */ });
        }
      } else if (!isVisible && !video.paused) {
        try { video.pause(); } catch { /* noop */ }
      }
    });
  }

  /**
   * Attempts to autoplay videos on initial load for mobile web.
   * Browsers may block this, but it's worth a try for a better UX.
   */
  private attemptInitialAutoplay(): void {
    this.videos.forEach((video) => {
      const rect = video.getBoundingClientRect();
      const isVisible = rect.top < window.innerHeight && rect.bottom > 0;

      if (isVisible && video.paused) {
        video.muted = true;
        const playPromise = video.play();
        if (playPromise !== undefined) {
          playPromise.catch(() => {
            // This is expected if browser blocks autoplay.
            // The `setupVideoAutoplay` listeners will handle playback on user interaction.
          });
        }
      }
    });
  }

  /**
   * Pauses all videos.
   */
  private pauseAllVideos(): void {
    this.videos.forEach((video) => {
      if (!video.paused) {
        try {
          video.pause();
        } catch { /* noop */ }
      }
    });
  }

  /**
   * For native mobile apps, uses Capacitor listeners to control video playback
   * when the app is backgrounded or resumed.
   */
  private async setupCapacitorVideoControls(): Promise<void> {
    this.appStateListener = await App.addListener('appStateChange', ({ isActive }) => {
      this.zone.run(() => {
        if (isActive) {
          // App resumed, treat as a fresh interaction and play visible videos
          this.hasUserInteraction = true;
          this.checkAndPlayVideos();
        } else {
          // App is in the background, pause all videos to save resources
          this.pauseAllVideos();
        }
      });
    });
  }

  /**
   * Handler for manual video playback (used in HTML)
   */
  playVideo(event: Event) {
    const video = event.target as HTMLVideoElement;
    video.muted = true;
    const playPromise = video.play();
    if (playPromise !== undefined) {
      playPromise.catch(() => { /* suppressed */ });
    }
  }

  /**
   * Initializes scroll animations
   */
  initScrollAnimation(): void {
    ScrollTrigger.matchMedia({
      // Mobile animation for phones (most restrictive first)
      '(max-width: 767px)': () => {
        console.log('LANDING: Applying mobile phone animation');
        this.createMobileLandingAnimation(false);
        this.createAdvertisingAnimation();
        this.createLandingFadeOutAnimation();
        ScrollTrigger.refresh();
        return () => {
          ['landingPinMobile', 'advertisingReveal', 'headerInvert', 'landingFadeOut', 'zIndexControl']
            .forEach(id => ScrollTrigger.getById(id)?.kill());
        };
      },

      // Mobile animation for portrait tablets (768px-1199px in portrait)
      '(min-width: 768px) and (max-width: 1199px) and (orientation: portrait)': () => {
        console.log('LANDING: Applying portrait tablet animation');
        this.createMobileLandingAnimation(true);
        this.createAdvertisingAnimation();
        this.createLandingFadeOutAnimation();
        ScrollTrigger.refresh();
        return () => {
          ['landingPinMobile', 'advertisingReveal', 'headerInvert', 'landingFadeOut', 'zIndexControl']
            .forEach(id => ScrollTrigger.getById(id)?.kill());
        };
      },

      // Desktop animation for wide screens (1200px+) or landscape tablets
      '(min-width: 1200px), ((min-width: 768px) and (orientation: landscape))': () => {
        console.log('LANDING: Applying desktop/landscape tablet animation');
        ScrollTrigger.normalizeScroll(true);
        this.createMainLandingAnimation();
        this.createAdvertisingAnimation();
        // this.createLandingFadeOutAnimation();
        ScrollTrigger.refresh();
        return () => {
          ScrollTrigger.normalizeScroll(false);
          ['landingPinDesktop', 'advertisingReveal', 'headerInvert'].forEach(id => ScrollTrigger.getById(id)?.kill());
        };
      },
    });
  }

  private isMobileOrTabletPortrait(): boolean {
    const isMobile = window.innerWidth <= 767;
    const isTabletPortrait = window.matchMedia('(min-width: 768px) and (max-width: 1199px) and (orientation: portrait)').matches;
    console.log(`LANDING: isMobileOrTabletPortrait check: innerWidth=${window.innerWidth}, isMobile=${isMobile}, isTabletPortrait=${isTabletPortrait}`);
    return isMobile || isTabletPortrait;
  }

  /**
   * Creates main landing animation (text and video transitions)
   */
  private createMainLandingAnimation(): void {
    const mainTimeline = gsap.timeline({
      scrollTrigger: {
    id: 'landingPinDesktop',
        trigger: '.landing-container',
        start: 'top top',
        end: '+=400%',
        scrub: 1,
        pin: true,
      },
    });

    // PHASE 1A: Text1 exits upward
    mainTimeline.to(
      '.text1',
      {
        opacity: 0,
        scale: 0.8,
        y: -30, // moves up
        duration: this.ANIMATION_TIMING.TEXT1_EXIT_DURATION,
        ease: 'power2.in',
      },
      this.ANIMATION_TIMING.TEXT1_EXIT_START,
    );

    // PHASE 1B: Text2 enters from bottom (separate phase)
    mainTimeline.fromTo(
      '.text2',
      {
        opacity: 0,
        scale: 0.7,
        y: 40, // starts from bottom
      },
      {
        opacity: 1,
        scale: 1,
        y: 0, // moves to final position
        duration: this.ANIMATION_TIMING.TEXT2_ENTER_DURATION,
        ease: 'power2.out',
      },
      this.ANIMATION_TIMING.TEXT2_ENTER_START,
    );

    // PHASE 2: Hide video1, show monitors, switch from first phone to second phone, shrink container (0.4-0.5)
    mainTimeline
      .to(
        '.video1',
        {
          opacity: 0,
          duration: this.ANIMATION_TIMING.PHASE_2_DURATION,
        },
        this.ANIMATION_TIMING.PHASE_2_START,
      )
      .to(
        '.monitor-group',
        {
          opacity: 1,
          duration: this.ANIMATION_TIMING.PHASE_2_DURATION,
        },
        this.ANIMATION_TIMING.PHASE_2_START,
      )
      .to(
        '.first-screen-phone-video',
        {
          opacity: 0,
          duration: this.ANIMATION_TIMING.PHASE_2_DURATION,
        },
        this.ANIMATION_TIMING.PHASE_2_START,
      )
      .to(
        '.second-screen-phone-image',
        {
          opacity: 1,
          duration: this.ANIMATION_TIMING.PHASE_2_DURATION,
        },
        this.ANIMATION_TIMING.PHASE_2_START,
      )
      .to(
        '.image-container',
        {
          scale: 0.66,
          duration: this.ANIMATION_TIMING.PHASE_3_DURATION,
        },
        this.ANIMATION_TIMING.PHASE_3_START,
      )
      .to(
        '.phone-composition',
        {
          left: '74.7%',
          duration: this.ANIMATION_TIMING.PHASE_3_DURATION,
        },
        this.ANIMATION_TIMING.PHASE_3_START,
      );

    // PHASE 3: Background-videos appear from screen center (0.5-1.0)
    mainTimeline.fromTo(
      '.background-videos video',
      {
        // Initial position: all videos in screen center
        x: (i, target) =>
          window.innerWidth / 2 - (target.offsetLeft + target.offsetWidth / 2),
        y: (i, target) =>
          window.innerHeight / 2 - (target.offsetTop + target.offsetHeight / 2),
    scale: 0,
    opacity: 1, // Show videos instantly when animation starts
      },
      {
        // Final position: each video in its place
        x: 0,
        y: 0,
        scale: 1,
        opacity: 1,
        stagger: { amount: 0.15, from: 'center' }, // Animation from center
        duration: this.ANIMATION_TIMING.PHASE_3_DURATION,
      },
      this.ANIMATION_TIMING.PHASE_3_START,
    );

    // PHASE 4: Return container to normal size, hide text2, monitors, switch to third phone screen (1.5)
    mainTimeline
      .to(
        '.image-container',
        {
          scale: 1,
          duration: this.ANIMATION_TIMING.PHASE_4_DURATION,
        },
        this.ANIMATION_TIMING.PHASE_4_START,
      )
      .to(
        '.phone-composition',
        {
          left: '81.7%',
          duration: this.ANIMATION_TIMING.PHASE_4_DURATION,
        },
        this.ANIMATION_TIMING.PHASE_4_START,
      )
      .to(
        '.text2',
        {
          opacity: 0,
          scale: 0.8,
          y: -30, // moves up
          duration: this.ANIMATION_TIMING.TEXT2_EXIT_DURATION,
          ease: 'power2.in',
        },
        this.ANIMATION_TIMING.TEXT2_EXIT_START,
      )
      .to(
        '.monitor-group',
        {
          opacity: 0,
          duration: this.ANIMATION_TIMING.PHASE_2_DURATION,
        },
        this.ANIMATION_TIMING.PHASE_4_START,
      )
      .to(
        '.video2',
        {
          opacity: 1,
          duration: this.ANIMATION_TIMING.PHASE_2_DURATION,
        },
        this.ANIMATION_TIMING.PHASE_4_START,
      )
      .to(
        '.second-screen-phone-image',
        {
          opacity: 0,
          duration: this.ANIMATION_TIMING.PHASE_2_DURATION,
        },
        this.ANIMATION_TIMING.PHASE_4_START,
      )
      .to(
        '.third-screen-phone-video',
        {
          opacity: 1,
          duration: this.ANIMATION_TIMING.PHASE_2_DURATION,
        },
        this.ANIMATION_TIMING.PHASE_4_START,
      );

    // PHASE 4B: Text3 enters from bottom (separate phase)
    mainTimeline.fromTo(
      '.text3',
      {
        opacity: 0,
        scale: 0.7,
        y: 40, // starts from bottom
      },
      {
        opacity: 1,
        scale: 1,
        y: 0, // moves to final position
        duration: this.ANIMATION_TIMING.TEXT3_ENTER_DURATION,
        ease: 'power2.out',
      },
      this.ANIMATION_TIMING.TEXT3_ENTER_START,
    );

    // PHASE 5: Text4 appears separately
    mainTimeline.fromTo(
      '.text4',
      {
        opacity: 0,
        scale: 0.8,
        y: 20, // smaller offset for subtitle
      },
      {
        opacity: 1,
        scale: 1,
        y: 0,
        duration: 0.3,
        ease: 'power2.out',
      },
      this.ANIMATION_TIMING.TEXT4_DELAY,
    );

    // PHASE 6: Hide background-videos back to center (1.5)
    mainTimeline.to(
      '.background-videos video',
      {
        x: (i, target) =>
          window.innerWidth / 2 - (target.offsetLeft + target.offsetWidth / 2),
        y: (i, target) =>
          window.innerHeight / 2 - (target.offsetTop + target.offsetHeight / 2),
        scale: 0,
        opacity: 0,
        stagger: { amount: 0.15, from: 'center' },
        duration: this.ANIMATION_TIMING.PHASE_6_DURATION,
      },
      this.ANIMATION_TIMING.PHASE_4_START,
    );
  }

  /**
   * Creates animation for advertising section
   */
  private createAdvertisingAnimation(): void {
    const header = document.querySelector('header');
    this.createHeaderInversionTrigger(header);

    // CRITICAL: Verify section exists before animating
    const advertisingSection = document.querySelector('.our-pricing-section');
    if (!advertisingSection) {
      console.warn('⚠️ LANDING: .our-pricing-section not found, skipping animation');
      return;
    }

    // Force a layout recalculation to ensure accurate measurements
    void advertisingSection.clientHeight;

    // Set initial state only for child elements
    gsap.set('.price-section-title', { opacity: 0, y: 50 });
    gsap.set('.feature-card', { opacity: 0, y: 50 });
    gsap.set('.cta-section', { opacity: 0, y: 50 });

    const advertisingTimeline = gsap.timeline({
      scrollTrigger: {
        id: 'advertisingReveal',
        trigger: '.our-pricing-section',
        start: 'top 80%',
        end: 'top 40%',
        scrub: 1,
        anticipatePin: 1,
        invalidateOnRefresh: true,
        // Ensure trigger waits for full layout
        // onRefresh: (self) => {
        //   console.log('🔄 LANDING: advertisingReveal refreshed, start:', self.start, 'end:', self.end);
        // },
      },
    });

    // Sequential appearance of child elements only
    advertisingTimeline
      .to('.price-section-title', { opacity: 1, y: 0 }, 0)
      .to('.feature-card', { opacity: 1, y: 0, stagger: 0.2 }, 0.2)
      .to('.cta-section', { opacity: 1, y: 0 }, 0.6);
  }

  /**
   * Creates a parallax scroll-away effect for the landing container
   */
  private createLandingFadeOutAnimation(): void {
    const landingContainer = document.querySelector('.landing-container');
    if (!landingContainer) return;

    gsap.timeline({
      scrollTrigger: {
        id: 'landingFadeOut',
        trigger: '.our-pricing-section',
        start: 'top bottom', // Starts when advertising section enters the viewport
        end: 'top 30%',      // Ends when advertising section covers 70% of the viewport
        scrub: 1,
      },
    }).to(landingContainer, {
      yPercent: -30, // Move up by 30% of its own height
      opacity: 0,
      ease: 'power1.in',
    });
  }

  /**
   * Dedicated header inversion trigger spanning entire advertising section
   */
  private createHeaderInversionTrigger(header: Element | null): void {
    ScrollTrigger.create({
      id: 'headerInvert',
      trigger: '.our-pricing-section',
        start: 'top 100vh', // When section is about to enter viewport
      end: 'bottom 0%',
  anticipatePin: 1,
      invalidateOnRefresh: true,
      onEnter: () => header?.classList.add('inverted'),
      onEnterBack: () => header?.classList.add('inverted'),
      onLeave: () => header?.classList.remove('inverted'),
      onLeaveBack: () => header?.classList.remove('inverted'),
    });
  }

  /**
   * Creates mobile-specific scroll animations
   */
  private createMobileLandingAnimation(isTabletPortrait = false): void {
    const onUpdateHandler = () => {
      this.checkAndPlayVideos();
    };
    const imageContainer = document.querySelector(
      '.image-container',
    ) as HTMLElement;
    if (!imageContainer) {
      return;
    }
    const computedStyle = getComputedStyle(imageContainer);
    const monitorSettleX =
      computedStyle.getPropertyValue('--monitor-settle-x').trim() || '-58%';
    const phoneInitialScale =
      parseFloat(
        computedStyle.getPropertyValue('--phone-initial-scale').trim(),
      ) || 1.7;
    const phoneSettleScale =
      parseFloat(
        computedStyle.getPropertyValue('--phone-settle-scale').trim(),
      ) || 0.62;
    const phoneInitialTopOffset =
      computedStyle.getPropertyValue('--phone-initial-top-offset').trim() || '0px';

    const tl = gsap.timeline({
      scrollTrigger: {
        id: 'landingPinMobile',
        trigger: '.mobile-scroll-spacer',
        // Add half of the safe-area inset top to the 40px offset for iOS notches
        start: 'top top',
        end: 'bottom bottom',
        scrub: 1,
        pin: false, // Pinning is disabled as the container is already fixed
        invalidateOnRefresh: true,
        anticipatePin: 1,
        onUpdate: onUpdateHandler,
        onRefresh: () => {
          this.createScrollSpacer();
        }
      },
    });

    this.addMobileAnimationStages(tl, monitorSettleX, phoneInitialScale, phoneSettleScale, phoneInitialTopOffset, isTabletPortrait);
    this.createZIndexControlTrigger();
  }

  /**
   * Adds animation stages for the mobile version.
   */
  private addMobileAnimationStages(tl: gsap.core.Timeline, monitorSettleX: string, phoneInitialScale: number, phoneSettleScale: number, phoneInitialTopOffset: string, isTabletPortrait = false): void {
    if (isTabletPortrait) {
      // For tablets, just set the initial state of stage 1 and skip its animation.
      gsap.set('.desktop-view', { x: monitorSettleX });
      gsap.set('.phone-composition', { scale: phoneSettleScale });
    } else {
    // Stage 1: bring in desktop view first, and phone settles to its target size/position
    tl.fromTo(
      '.desktop-view',
      { x: '-120%' },
      { x: monitorSettleX, ease: 'power2.out', duration: 1.2 },
      0,
    ).fromTo(
      '.phone-composition',
      {
        scale: phoneInitialScale, // start with initial scale
        x: '0%',
        top: phoneInitialTopOffset,
      },
      {
        scale: phoneSettleScale, // animate to settle scale
        x: '0%',
        top: '0px',
        ease: 'power2.out',
        duration: 1,
      },
      0.2,
    );
    }

    // Stage 2A: text1 exits upward
    tl.to('.text1', {
        opacity: 0,
        scale: 0.8,
        y: -30, // moves up
        duration: 0.3,
        ease: 'power2.in'
      }, '>-0.05');

    // --- Start of combined Phase 2 ---
     // Stage 2B: text2 enters from bottom (separate phase)
    tl.fromTo('.text2',
        {
          opacity: 0,
          scale: 0.7,
          y: 40, // starts from bottom
        },
        {
          opacity: 1,
          scale: 1,
          y: 0, // moves to final position
          duration: 0.4,
          ease: 'power2.out'
        }, '>+0.1');

    // Stage 3: swap dynamic media to static (like desktop)
    tl.to('.video1', { opacity: 0, duration: 0.2 }, '>-0.05')
      .to('.monitor-group', { opacity: 1, duration: 0.2 }, '<')
      .to('.first-screen-phone-video', { opacity: 0, duration: 0.2 }, '<')
      .to('.second-screen-phone-image', { opacity: 1, duration: 0.2 }, '<');

    // Stage 4: reveal shared videos from center (desktop-style)
    const phase2StartTime = '>+0.1';
    tl.fromTo(
      '.background-videos video',
      {
        x: (i, target: any) =>
          window.innerWidth / 2 - (target.offsetLeft + target.offsetWidth / 2),
        y: (i, target: any) =>
          window.innerHeight / 2 - (target.offsetTop + target.offsetHeight / 2),
        scale: 0,
        opacity: 0,
      },
      {
        x: 0,
        y: 0,
        scale: 1,
        opacity: 1,
        stagger: { amount: 0.15, from: 'center' },
        duration: 0.6,
      },
      phase2StartTime,
    );

    if (isTabletPortrait) {
      // Create a separate timeline for the tablet-specific animations
      // to run them in parallel without affecting the main timeline's sequence.
      const tabletMovementTl = gsap.timeline();
      tabletMovementTl
        .to('.desktop-view', {
          x: '-75%', // Center desktop view more
          ease: 'power2.out',
          duration: 1.2,
        }, 0) // Start at the beginning of this sub-timeline
        .to('.phone-composition', {
          x: '-35%', // Move phone to the right
          ease: 'power2.out',
          duration: 1.2,
        }, 0); // Also start at the beginning

      // Add this sub-timeline to the main timeline at the start of the text2 animation
      tl.add(tabletMovementTl, '<');
    }

    // Stage 5A: text2 exits upward
    tl.to('.text2', {
        opacity: 0,
        scale: 0.8,
        y: -30, // moves up
        duration: 0.3,
        ease: 'power2.in'
      }, '+=0.4');

    // Stage 5B: text3 enters from bottom (separate phase)
    tl.fromTo('.text3',
        {
          opacity: 0,
          scale: 0.7,
          y: 40, // starts from bottom
        },
        {
          opacity: 1,
          scale: 1,
          y: 0, // moves to final position
          duration: 0.4,
          ease: 'power2.out'
        }, '>+0.1');

    // Stage 5C: text4 appears separately
    tl.fromTo('.text4',
        {
          opacity: 0,
          scale: 0.8,
          y: 20, // smaller offset for subtitle
        },
        {
          opacity: 1,
          scale: 1,
          y: 0,
          duration: 0.3,
          ease: 'power2.out'
        }, '>+0.1');

    // Hide background videos - this runs for both phones and tablets
    tl.to('.background-videos video', {
      opacity: 0,
      scale: 0,
      stagger: { amount: 0.1, from: 'center' },
      duration: 0.4,
    }, '<');

    if (isTabletPortrait) {
      // On tablets, revert the earlier movement to the settled state.
      const tabletRevertTl = gsap.timeline();
      tabletRevertTl
        .to('.desktop-view', {
          x: monitorSettleX, // This is '-58%'
          ease: 'power2.out',
          duration: 1.2,
        }, 0)
        .to('.phone-composition', {
          x: '0%',
          ease: 'power2.out',
          duration: 1.2,
        }, 0);
      tl.add(tabletRevertTl, '<');
    } else {
      // On phones, do the final pan out.
      tl.to('.desktop-view', {
        x: '-43%', // Center desktop view more
        ease: 'power2.out',
        duration: 1.2,
      }, '<')
        .to(
          '.phone-composition',
          {
            scale: phoneSettleScale, // Keep phone slightly larger
            x: '30%', // Move phone to the right
            ease: 'power2.out',
            duration: 1.2,
          },
          '<',
        );
    }

    tl.to('.monitor-group', { opacity: 0, duration: 0.2 }, '<')
      .to('.video2', { opacity: 1, duration: 0.2 }, '<')
      .to('.second-screen-phone-image', { opacity: 0, duration: 0.2 }, '<')
      .to('.third-screen-phone-video', { opacity: 1, duration: 0.2 }, '<');
  }

  // Returns half of the CSS safe area inset top in pixels, or 0 if unavailable
  private getSafeInsetTopHalf(): number {
    try {
      const style = getComputedStyle(document.documentElement);
      const raw = style.getPropertyValue('--safe-area-inset-top')?.trim();
      if (!raw) return 0;
      // Expecting a pixel value like "20px"; parseFloat gracefully ignores the 'px'
      const px = parseFloat(raw);
      if (Number.isNaN(px)) return 0;
      return Math.max(0, px / 2);
    } catch {
      return 0;
    }
  }

  /**
   * Prepares the mobile layout for smooth pinning.
   */
  private prepareMobileLayout(): void {
    const landingContainer = document.querySelector('.landing-container') as HTMLElement;
    if (landingContainer) {
      landingContainer.classList.add('prepare-pin');
      this.createScrollSpacer();
    }
  }

  /**
   * Resets the mobile layout.
   */
  private resetMobileLayout(): void {
    const landingContainer = document.querySelector('.landing-container') as HTMLElement;
    if (landingContainer) {
      landingContainer.classList.remove('prepare-pin', 'animation-complete');
    }

    this.removeScrollSpacer();
  }

  /**
   * Creates a spacer to compensate for fixed positioning.
   */
  private createScrollSpacer(): void {
    let spacer = document.querySelector('.mobile-scroll-spacer') as HTMLElement;
    if (!spacer) {
      spacer = document.createElement('div');
      spacer.className = 'mobile-scroll-spacer';
      spacer.style.height = '400vh'; // Corresponds to end: '+=300%' in ScrollTrigger
      spacer.style.pointerEvents = 'none';
      spacer.style.position = 'relative';
      spacer.style.zIndex = '-1';

      const landingContainer = document.querySelector('.landing-container');
      if (landingContainer && landingContainer.parentNode) {
        landingContainer.parentNode.insertBefore(spacer, landingContainer.nextSibling);
      }
    }
  }

  /**
   * Removes the spacer.
   */
  private removeScrollSpacer(): void {
    const spacer = document.querySelector('.mobile-scroll-spacer');
    if (spacer && spacer.parentNode) {
      spacer.parentNode.removeChild(spacer);
    }
  }

  /**
   * Creates a trigger to control the container's z-index at the end of the animation.
   */
  private createZIndexControlTrigger(): void {
    const landingContainer = document.querySelector('.landing-container') as HTMLElement;
    if (!landingContainer) return;

    ScrollTrigger.create({
      id: 'zIndexControl',
      trigger: '.mobile-scroll-spacer',
      start: 'bottom bottom-=100px', // A little before the end for smoothness
      end: 'bottom bottom',
      onEnter: () => {
        // Animation is complete, hide the container behind the our-pricing-section
        landingContainer.classList.add('animation-complete');
      },
      onLeaveBack: () => {
        // Returning to the animation, show the container
        landingContainer.classList.remove('animation-complete');
      },
      invalidateOnRefresh: true,
    });
  }

  // Auth-related methods
  onSignInClicked(): void {
    this.authModalService.openAuthModal(true, true); // redirect after auth
  }

  onTryForFreeClicked(): void {
    this.authModalService.openAuthModal(false, true); // redirect after auth
  }
}
